import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Icon from '../components/ui/Icon';
import Button from '../components/ui/Button';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { EmptyState, ErrorState } from '../components/ui/States';
import ReceiptViewer from '../components/scans/ReceiptViewer';
import OcrCard from '../components/scans/OcrCard';
import ScanFields from '../components/scans/ScanFields';
import ScanHeader from '../components/scans/ScanHeader';
import { PriceCard, UserInfoCard } from '../components/scans/ScanSideCards';
import { ApproveDialog, RejectDialog } from '../components/scans/ScanDialogs';
import useUnsavedGuard from '../components/scans/useUnsavedGuard';
import { scanDetailKey } from '../components/scans/useScans';
import { detailPath, listPath } from '../components/scans/scansParams';
import { formatAmsterdam, toAmsterdamInput, toApiWallTime } from '../components/scans/amsterdamTime';
import { formatAmount, formatPrice, formatVolume, safeUrl } from '../components/scans/scanAdapter';
import {
  changedFields,
  fieldId,
  FIELDS,
  firstErrorField,
  mapServerErrors,
  parseNumber,
  requiredMessage,
  toForm,
  validateForm,
} from '../components/scans/scanRules';
import { errorCode, scansApi } from '../api/scans';
import { stationsApi } from '../api/stations';
import { ApiError } from '../api/client';
import { optionName } from '../components/stations/stationAdapter';

const FIELD_LABELS = {
  fuelOptionId: 'Fuel',
  unitPrice: 'Unit price',
  volume: 'Volume',
  totalAmount: 'Total',
  receiptAt: 'Receipt time',
  receiptNumber: 'Ticket number',
};

const isTyping = (el) => el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));

function focusField(field) {
  const el = field && document.getElementById(fieldId(field));
  if (!el) return;
  el.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  el.focus({ preventScroll: true });
}

function Alert({ tone = 'alert', children }) {
  const styles = {
    alert: 'border-status-alert-border bg-status-alert-bg text-status-alert-text',
    pending: 'border-status-pending-border bg-status-pending-bg text-status-pending-text',
  };
  return (
    <div role="alert" className={`flex items-start gap-2 rounded-xl border px-4 py-3 text-sm ${styles[tone]}`}>
      <Icon name={tone === 'alert' ? 'error' : 'warning'} size={18} className="shrink-0 mt-px" />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

/** One scan under review. Keyed by id, so moving to another scan starts fresh. */
function ScanReview({ scanId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const listSearch = location.search;
  const key = scanDetailKey(scanId);

  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => scansApi.getScan(scanId, { signal }),
    staleTime: 0,
    refetchOnWindowFocus: false, // never replace the form under the admin's hands
    retry: (count, err) => (err?.status === 0 || err?.status >= 500) && count < 1,
  });
  const fuelOptionsQuery = useQuery({
    queryKey: ['fuel-options'],
    queryFn: ({ signal }) => stationsApi.getFuelOptions(undefined, { signal }),
    staleTime: 5 * 60_000,
  });
  const scan = query.data;

  // The form follows the loaded scan; it's only reset on purpose (first load, save, approve, 409)
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [attempted, setAttempted] = useState(false);
  const [generalError, setGeneralError] = useState(null);
  const [duplicate, setDuplicate] = useState(null);
  const [lostEdits, setLostEdits] = useState(null);
  const [dialog, setDialog] = useState(null); // 'approve' | 'reject'
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (scan && form === null) setForm(toForm(scan));
  }, [scan, form]);

  const resetFormFrom = (next) => {
    queryClient.setQueryData(key, next);
    setForm(toForm(next));
    setErrors({});
    setAttempted(false);
  };

  const pending = scan?.status === 'pending';
  const changes = useMemo(() => (form && scan ? changedFields(form, scan) : {}), [form, scan]);
  const dirty = pending && Object.keys(changes).length > 0;
  const guard = useUnsavedGuard(dirty);

  const setField = (field, value) => {
    const next = { ...form, [field]: value ?? '' };
    setForm(next);
    setGeneralError(null);
    if (attempted) setErrors(validateForm(next, { initial: toForm(scan) }));
  };

  // Stay on this scan and reload it, so the page shows the reviewed (read-only) state
  const afterReview = (result, verb) => {
    queryClient.invalidateQueries({ queryKey: ['scans'] });
    resetFormFrom(result.scan);
    setGeneralError(null);
    setDuplicate(null);
    toast.success(result.message || `Scan #${scanId} ${verb}`);
    query.refetch().then(({ data }) => data && resetFormFrom(data));
  };

  const handleError = (err) => {
    const code = errorCode(err);
    const message = err instanceof ApiError ? err.message : 'Something went wrong. Please try again.';
    setDialog(null);
    if (code === 'SCAN_NOT_PENDING') {
      toast.error(message);
      if (Object.keys(changes).length) setLostEdits(changes);
      query.refetch().then(({ data }) => data && resetFormFrom(data));
      return;
    }
    if (code === 'MISSING_FIELDS') {
      const fields = (err.data?.Data?.fields ?? []).filter((f) => FIELDS.includes(f));
      const missing = Object.fromEntries(fields.map((f) => [f, requiredMessage(f)]));
      setErrors((e) => ({ ...e, ...missing }));
      setAttempted(true);
      setGeneralError(message);
      toast.error(message);
      requestAnimationFrame(() => focusField(firstErrorField(missing)));
      return;
    }
    if (code === 'DUPLICATE_RECEIPT') {
      setDuplicate(err.data?.Data?.duplicateOf ?? { id: null });
      toast.error(message);
      return;
    }
    if (err?.status === 400 && err.fieldErrors?.length) {
      const mapped = mapServerErrors(err.fieldErrors);
      setErrors(mapped.errors);
      setAttempted(true);
      setGeneralError(mapped.general.length ? mapped.general.join(' ') : null);
      requestAnimationFrame(() => focusField(firstErrorField(mapped.errors)));
    } else {
      setGeneralError(message);
    }
    toast.error(message);
  };

  const save = useMutation({
    mutationFn: () => scansApi.saveScanEdits(scanId, changes),
    onMutate: () => setBusy('save'),
    onSuccess: (result) => {
      resetFormFrom(result.scan);
      queryClient.invalidateQueries({ queryKey: ['scans'] });
      toast.success('Changes saved');
    },
    onError: handleError,
    onSettled: () => setBusy(null),
  });

  const approve = useMutation({
    mutationFn: () => scansApi.approveScan(scanId, changes),
    onMutate: () => setBusy('approve'),
    onSuccess: (result) => {
      setDialog(null);
      if (result.scan.priceApplied === false) toast.info(result.message || "The station already had a newer price, so it wasn't changed.");
      afterReview({ ...result, message: result.scan.priceApplied === false ? `Scan #${scanId} approved` : result.message }, 'approved');
    },
    onError: handleError,
    onSettled: () => setBusy(null),
  });

  const reject = useMutation({
    mutationFn: (reason) => scansApi.rejectScan(scanId, reason),
    onMutate: () => setBusy('reject'),
    onSuccess: (result) => {
      setDialog(null);
      afterReview(result, 'rejected');
    },
    onError: handleError,
    onSettled: () => setBusy(null),
  });

  const runValidation = (opts) => {
    setAttempted(true);
    const found = validateForm(form, { ...opts, initial: toForm(scan) });
    setErrors(found);
    setGeneralError(null);
    const first = firstErrorField(found);
    if (first) focusField(first);
    return !first;
  };

  const startSave = () => {
    if (busy || !dirty) return;
    if (runValidation()) save.mutate();
  };
  const startApprove = () => {
    if (busy || !pending) return;
    setDuplicate(null);
    if (!runValidation({ approve: true })) return;
    setDialog('approve');
  };
  const startReject = () => {
    if (busy || !pending) return;
    setDialog('reject');
  };
  const goTo = (id) => id && guard.guardedNavigate(detailPath(id, listSearch));

  // Keyboard shortcuts, ignored while typing or when a dialog is open
  const shortcuts = useRef({});
  shortcuts.current = {
    a: startApprove,
    r: startReject,
    e: () => pending && focusField(FIELDS[0]),
    n: () => goTo(scan?.queue?.nextId),
  };
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target)) return;
      if (document.querySelector('[aria-modal="true"]')) return;
      const action = shortcuts.current[e.key.toLowerCase()];
      if (!action) return;
      e.preventDefault();
      action();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  if (query.error) {
    return query.error.status === 404 ? (
      <EmptyState
        tone="neutral"
        icon="search_off"
        title={`Scan #${scanId} not found`}
        description="It may have been deleted."
        action={
          <Button variant="secondary" icon="arrow_back" onClick={() => navigate(listPath(listSearch))}>
            Back to scans
          </Button>
        }
      />
    ) : (
      <ErrorState title="Couldn't load this scan" description={query.error.message} onRetry={() => query.refetch()} />
    );
  }
  if (!scan || !form) {
    return (
      <div className="flex flex-col gap-6 animate-pulse" role="status" aria-label="Loading scan">
        <div className="h-20 rounded-xl bg-surface-container-low" />
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          <div className="xl:col-span-5 h-[640px] rounded-xl bg-surface-container-low" />
          <div className="xl:col-span-7 flex flex-col gap-6">
            <div className="h-36 rounded-xl bg-surface-container-low" />
            <div className="h-96 rounded-xl bg-surface-container-low" />
          </div>
        </div>
      </div>
    );
  }

  const formPrice = (() => {
    const n = parseNumber(form.unitPrice);
    return typeof n === 'number' && !Number.isNaN(n) ? n : null;
  })();
  const options = fuelOptionsQuery.data ?? [];
  const selectedOption = options.find((o) => String(o.id) === form.fuelOptionId);
  const fuelLabel = selectedOption ? optionName(selectedOption) : form.fuelOptionId === String(scan.fuelOption?.id) ? scan.fuelOption?.name : null;
  const unit = selectedOption?.unit || scan.fuelOption?.unit || 'L';

  const newerStationPrice =
    scan.currentPrice?.updatedAt && form.receiptAt && toAmsterdamInput(scan.currentPrice.updatedAt) > (toApiWallTime(form.receiptAt) ?? '');
  const summary = [
    ['Fuel', fuelLabel ? `${fuelLabel}${selectedOption ? ` - ${selectedOption.code}` : ''}` : '—'],
    ['Unit price', `${formatPrice(formPrice)} / ${unit}`],
    ['Volume', formatVolume(parseNumber(form.volume), unit)],
    ['Total', formatAmount(parseNumber(form.totalAmount))],
    ['Receipt time', form.receiptAt ? `${toApiWallTime(form.receiptAt).replace('T', ' ')} (NL)` : '—'],
    ['Ticket number', form.receiptNumber || '—'],
  ];

  return (
    <div className="flex flex-col gap-6">
      <ScanHeader
        scan={scan}
        dirty={dirty}
        busy={busy}
        formPrice={formPrice}
        // Back in history when the admin came from inside the app; a direct link has no history, so go to the list
        onBack={() => guard.guardedNavigate(location.key !== 'default' ? -1 : listPath(listSearch))}
        onReject={startReject}
        onSave={startSave}
        onApprove={startApprove}
      />

      {lostEdits && (
        <Alert tone="pending">
          <p className="font-semibold">This scan was already reviewed, so your edits weren&apos;t saved:</p>
          <ul className="mt-1 font-data-mono text-xs space-y-0.5">
            {Object.entries(lostEdits).map(([field, value]) => (
              <li key={field}>
                {FIELD_LABELS[field]}: {value === null ? '(cleared)' : String(value)}
              </li>
            ))}
          </ul>
          <button type="button" onClick={() => setLostEdits(null)} className="mt-2 text-xs font-semibold underline underline-offset-2">
            Dismiss
          </button>
        </Alert>
      )}
      {duplicate && (
        <Alert>
          This receipt is already approved in{' '}
          {duplicate.id ? (
            <Link to={detailPath(duplicate.id, listSearch)} className="font-semibold underline underline-offset-2">
              scan #{duplicate.id}
            </Link>
          ) : (
            'another scan'
          )}
          . Reject this one as a duplicate?{' '}
          <button
            type="button"
            onClick={() => setDialog('reject-duplicate')}
            className="font-semibold underline underline-offset-2"
          >
            Reject as duplicate
          </button>
        </Alert>
      )}
      {generalError && <Alert>{generalError}</Alert>}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
        <div className="xl:col-span-5 flex flex-col gap-4 bg-surface-container-lowest rounded-xl border border-outline-variant/30 shadow-xs p-4">
          <ReceiptViewer url={safeUrl(scan.receiptUrl)} receiptNumber={scan.receiptNumber} />
          <OcrCard ocr={scan.ocr} ocrText={scan.ocrText} />
        </div>
        <div className="xl:col-span-7 flex flex-col gap-6 min-w-0">
          <PriceCard scan={scan} form={form} fuelLabel={fuelLabel} />
          <ScanFields scan={scan} form={form} errors={errors} editable={pending && !busy} fuelOptions={options} onChange={setField} />
          <UserInfoCard scan={scan} />
          {scan.submittedAt && (
            <p className="text-[11px] text-on-surface-variant">
              Submitted <span className="font-data-mono">{formatAmsterdam(scan.submittedAt)}</span> · created{' '}
              <span className="font-data-mono">{formatAmsterdam(scan.createdAt)}</span> (Netherlands time)
            </p>
          )}
        </div>
      </div>

      {dialog === 'approve' && (
        <ApproveDialog
          scanId={scanId}
          amount={formPrice !== null ? `${formatPrice(formPrice)}/${unit}` : null}
          summary={summary}
          priceNote={
            newerStationPrice
              ? { applies: false, text: "The station has a newer price; it won't change." }
              : { applies: true, text: "This updates the station's live price." }
          }
          busy={busy === 'approve'}
          onConfirm={() => approve.mutate()}
          onCancel={() => setDialog(null)}
        />
      )}
      {(dialog === 'reject' || dialog === 'reject-duplicate') && (
        <RejectDialog
          scanId={scanId}
          initialReason={dialog === 'reject-duplicate' ? 'Duplicate receipt' : ''}
          busy={busy === 'reject'}
          onConfirm={(reason) => reject.mutate(reason)}
          onCancel={() => setDialog(null)}
        />
      )}
      <ConfirmDialog
        open={Boolean(guard.pending)}
        title="Leave without saving?"
        description="Your edits to this scan haven't been saved."
        confirmLabel="Discard edits"
        cancelLabel="Keep editing"
        onConfirm={guard.confirm}
        onCancel={guard.cancel}
      />
    </div>
  );
}

/** /scan-details/:id: review one receipt scan. */
export default function ScanDetailsPage() {
  const { id } = useParams();
  const location = useLocation();
  const scanId = /^\d+$/.test(id ?? '') ? Number(id) : null;
  if (!scanId) {
    return (
      <EmptyState
        tone="neutral"
        icon="search_off"
        title="Scan not found"
        description={`"${id}" isn't a valid scan number.`}
        action={
          <Link to={listPath(location.search)} className="text-sm font-semibold text-secondary hover:underline">
            Back to scans
          </Link>
        }
      />
    );
  }
  return <ScanReview key={scanId} scanId={scanId} />;
}
